import { useEffect } from 'react'
import { useNavigate } from '@reach/router'

import useQuery from 'hooks/useQuery'
import useTeamID from 'hooks/useTeamID'
import { planFromSlug } from '../../Planes/content'

const INVALID_PLAN_REASONS = ['invalid_plan', 'plan_required']

/**
 * Plan de la ruta /suscripcion/<plan> (card 1053) y si el team puede contratarlo.
 *
 * - Resuelve el segmento contra el catálogo (pro, premium, esencial…); uno que no existe
 *   manda a Planes.
 * - Pregunta al api si el team puede contratarlo (api#564: permiso, suscripción existente,
 *   tope de agendas, aprobación de Ventas). `invalid_plan` / `plan_required` también mandan
 *   a Planes; el resto de los motivos los muestra el formulario. Si el chequeo falla, el
 *   formulario usa la lista de /subscriptions (`checkFailed`) para no duplicar la suscripción.
 *
 * Se espera la respuesta (o el error) de cada consulta, no `!isLoading`: mientras no hay
 * token useQuery la deja deshabilitada, sin datos y con isLoading en false, y al entrar
 * directo por la URL se mandaba a Planes un plan que existe.
 *
 * Fuera de Form.js porque la versión de eslint-plugin-react-hooks del repo cuenta mal los
 * caminos en componentes con muchas ramas y marca como condicionales hooks que no lo son.
 */
export default function useSubscriptionPlan(planSlug) {
  const navigate = useNavigate()
  const teamID = useTeamID().replace(/^teams\//, '')
  const catalog = useQuery(planSlug ? 'plans' : '', { staleTime: 60000, retry: false })
  const catalogReady = Boolean(catalog.data) || Boolean(catalog.isError)
  const plan = planSlug ? planFromSlug(planSlug, (catalog.data || {}).plans) : null
  const planCode = plan ? plan.code : undefined

  const check = useQuery(planCode && teamID ? `plans/team/${teamID}/subscription-check?plan=${planCode}` : '', {
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: false,
    timeout: 10000,
  })
  const checkReady = Boolean(check.data) || Boolean(check.isError)

  useEffect(() => {
    if (planSlug && catalogReady && !plan) navigate(window._l('/planes'), { replace: true })
  }, [planSlug, catalogReady, plan]) // eslint-disable-line react-hooks/exhaustive-deps

  const deniedReason = check.data && check.data.allowed === false ? check.data.reason : null
  useEffect(() => {
    if (INVALID_PLAN_REASONS.includes(deniedReason)) navigate(window._l('/planes'), { replace: true })
  }, [deniedReason]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    plan,
    planCode,
    // Con plan, hasta tener el catálogo y la respuesta del chequeo.
    loading: Boolean(planSlug) && (!plan || !checkReady),
    denied: deniedReason ? check.data : null,
    // El chequeo no respondió (ej. 404 sin api#564, timeout, 5xx): el formulario decide con
    // la lista de /subscriptions para no crear una segunda suscripción.
    checkFailed: Boolean(planCode) && Boolean(check.isError),
  }
}
